'use client'

import { useEffect, useRef } from 'react'

const BASE_SCALE = 32
const BASE_FREQ = '0.009 0.003'
const FREQ2 = '0.0135 0.0039'
const FREQ3 = '0.0198 0.0054'
const H = 1000
const RECT_PAD = 500
const RW = 1440 + RECT_PAD * 2

// At ~600px section height on desktop, BASE_SCALE looks right.
// Compensate for preserveAspectRatio="none" stretching the displacement
// on tall (mobile) or short (landscape) sections.
const CALIBRATION_HEIGHT = 600
const TARGET_DISP = (BASE_SCALE * CALIBRATION_HEIGHT) / H

function scaleForHeight(elHeight: number): number {
  const raw = (TARGET_DISP * H) / elHeight
  return Math.round(Math.min(48, Math.max(14, raw)))
}

function makeMaskSvg(edge: 'top' | 'bottom', seed: number, scale: number): string {
  const seed2 = seed + 37
  const seed3 = seed + 173
  const scale2 = Math.round(scale * 0.65)
  const scale3 = Math.round(scale * 0.4)
  const tornStart = H - 100

  if (edge === 'bottom') {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 ${H}" preserveAspectRatio="none">
  <defs>
    <filter id="f1" x="-20%" y="-30%" width="140%" height="160%" color-interpolation-filters="sRGB">
      <feTurbulence type="fractalNoise" baseFrequency="${BASE_FREQ}" numOctaves="4" seed="${seed}" stitchTiles="stitch" result="n"/>
      <feDisplacementMap in="SourceGraphic" in2="n" scale="${scale}" xChannelSelector="R" yChannelSelector="G" result="displaced"/>
      <feGaussianBlur in="displaced" stdDeviation="0.8"/>
    </filter>
    <filter id="f2" x="-20%" y="-30%" width="140%" height="160%" color-interpolation-filters="sRGB">
      <feTurbulence type="fractalNoise" baseFrequency="${FREQ2}" numOctaves="3" seed="${seed2}" stitchTiles="stitch" result="n"/>
      <feDisplacementMap in="SourceGraphic" in2="n" scale="${scale2}" xChannelSelector="R" yChannelSelector="G" result="displaced"/>
      <feGaussianBlur in="displaced" stdDeviation="0.6"/>
    </filter>
    <filter id="f3" x="-20%" y="-30%" width="140%" height="160%" color-interpolation-filters="sRGB">
      <feTurbulence type="fractalNoise" baseFrequency="${FREQ3}" numOctaves="3" seed="${seed3}" stitchTiles="stitch" result="n"/>
      <feDisplacementMap in="SourceGraphic" in2="n" scale="${scale3}" xChannelSelector="R" yChannelSelector="G" result="displaced"/>
      <feGaussianBlur in="displaced" stdDeviation="0.4"/>
    </filter>
  </defs>
  <rect x="${-RECT_PAD}" width="${RW}" height="${tornStart}" fill="white"/>
  <rect x="${-RECT_PAD}" y="${tornStart - 25}" width="${RW}" height="125" fill="white" filter="url(#f1)"/>
  <rect x="${-RECT_PAD}" y="${tornStart - 15}" width="${RW}" height="115" fill="white" opacity="0.5" filter="url(#f2)"/>
  <rect x="${-RECT_PAD}" y="${tornStart - 8}" width="${RW}" height="108" fill="white" opacity="0.3" filter="url(#f3)"/>
</svg>`
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 ${H}" preserveAspectRatio="none">
  <defs>
    <filter id="f1" x="-20%" y="-30%" width="140%" height="160%" color-interpolation-filters="sRGB">
      <feTurbulence type="fractalNoise" baseFrequency="${BASE_FREQ}" numOctaves="4" seed="${seed}" stitchTiles="stitch" result="n"/>
      <feDisplacementMap in="SourceGraphic" in2="n" scale="${scale}" xChannelSelector="R" yChannelSelector="G" result="displaced"/>
      <feGaussianBlur in="displaced" stdDeviation="0.8"/>
    </filter>
    <filter id="f2" x="-20%" y="-30%" width="140%" height="160%" color-interpolation-filters="sRGB">
      <feTurbulence type="fractalNoise" baseFrequency="${FREQ2}" numOctaves="3" seed="${seed2}" stitchTiles="stitch" result="n"/>
      <feDisplacementMap in="SourceGraphic" in2="n" scale="${scale2}" xChannelSelector="R" yChannelSelector="G" result="displaced"/>
      <feGaussianBlur in="displaced" stdDeviation="0.6"/>
    </filter>
    <filter id="f3" x="-20%" y="-30%" width="140%" height="160%" color-interpolation-filters="sRGB">
      <feTurbulence type="fractalNoise" baseFrequency="${FREQ3}" numOctaves="3" seed="${seed3}" stitchTiles="stitch" result="n"/>
      <feDisplacementMap in="SourceGraphic" in2="n" scale="${scale3}" xChannelSelector="R" yChannelSelector="G" result="displaced"/>
      <feGaussianBlur in="displaced" stdDeviation="0.4"/>
    </filter>
  </defs>
  <rect x="${-RECT_PAD}" y="100" width="${RW}" height="${H - 100}" fill="white"/>
  <rect x="${-RECT_PAD}" y="0" width="${RW}" height="125" fill="white" filter="url(#f1)"/>
  <rect x="${-RECT_PAD}" y="0" width="${RW}" height="115" fill="white" opacity="0.5" filter="url(#f2)"/>
  <rect x="${-RECT_PAD}" y="0" width="${RW}" height="108" fill="white" opacity="0.3" filter="url(#f3)"/>
</svg>`
}

function svgToDataUri(svg: string): string {
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}

// Track seed and last-applied height per element so we only regenerate on size change
const maskState = new WeakMap<Element, { seed: number; height: number }>()

function applyMask(el: Element) {
  const htmlEl = el as HTMLElement
  const elHeight = htmlEl.offsetHeight || 600

  const prev = maskState.get(el)
  if (prev && prev.height === elHeight) return

  const seed = prev?.seed ?? Math.floor(Math.random() * 10000)
  maskState.set(el, { seed, height: elHeight })

  const scale = scaleForHeight(elHeight)
  const hasTop = el.classList.contains('torn-top')
  const hasBottom = el.classList.contains('torn-bottom')

  if (hasTop && hasBottom) {
    const topUri = svgToDataUri(makeMaskSvg('top', seed, scale))
    const bottomUri = svgToDataUri(makeMaskSvg('bottom', seed + 500, scale))
    htmlEl.style.maskImage = `${topUri}, ${bottomUri}`
    htmlEl.style.setProperty('-webkit-mask-image', `${topUri}, ${bottomUri}`)
    htmlEl.style.maskSize = '100% 100%, 100% 100%'
    htmlEl.style.setProperty('-webkit-mask-size', '100% 100%, 100% 100%')
    htmlEl.style.maskRepeat = 'no-repeat, no-repeat'
    htmlEl.style.setProperty('-webkit-mask-repeat', 'no-repeat, no-repeat')
    htmlEl.style.maskComposite = 'intersect'
    htmlEl.style.setProperty('-webkit-mask-composite', 'source-in')
  } else if (hasBottom) {
    const uri = svgToDataUri(makeMaskSvg('bottom', seed, scale))
    htmlEl.style.maskImage = uri
    htmlEl.style.setProperty('-webkit-mask-image', uri)
  } else if (hasTop) {
    const uri = svgToDataUri(makeMaskSvg('top', seed, scale))
    htmlEl.style.maskImage = uri
    htmlEl.style.setProperty('-webkit-mask-image', uri)
  }
}

function applyAllMasks() {
  const sections = document.querySelectorAll('.torn-top, .torn-bottom')
  for (const el of sections) {
    applyMask(el)
  }
}

export function TornEdgeScroller(): null {
  const initialised = useRef(false)

  useEffect(() => {
    if (initialised.current) return
    initialised.current = true

    applyAllMasks()

    // Debounced rAF helper, waits for rapid events to settle, then runs once
    let debounceTimer = 0
    let raf = 0
    const debouncedApply = () => {
      clearTimeout(debounceTimer)
      cancelAnimationFrame(raf)
      debounceTimer = window.setTimeout(() => {
        raf = requestAnimationFrame(applyAllMasks)
      }, 200)
    }

    // Re-run on DOM changes (route navigation)
    const main = document.querySelector('main')
    const observer = main ? new MutationObserver(debouncedApply) : null
    observer?.observe(main!, { childList: true, subtree: true })

    // Regenerate masks on resize (orientation change, viewport resize)
    window.addEventListener('resize', debouncedApply)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', debouncedApply)
      clearTimeout(debounceTimer)
      cancelAnimationFrame(raf)
    }
  }, [])

  return null
}
